import { useFormContext } from "react-hook-form";
import { t } from "i18next";
import { Box, Button, useTheme } from "@mui/material";
import { isMobile, isTablet } from "react-device-detect";
import { MutableRefObject, Ref, forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { AttributeSchemaFormType, AttributeSchemaUsageActions, usageActionItems } from "../AttributeSchemaEditorUtils";
import AttributeSchemaObjectList, { AttributeSchemaObjectListType } from "./AttributeSchemaObjectList";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { BaseActions, ActionMenuItem } from "../../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";
import NoData from "../../no-data/NoData";
import TabBar from "../../tab-bar/TabBar";
import TabContainer from "../../tab-container/TabContainer";
import TabWithCount from "../../tab-with-count/TabWithCount";
import { UseAttributeSchemaObjectStateType, AttributeSchemaObjectTabEnum } from "./AttributeSchemaObjectState";

export type AttributeSchemaObjectsType = {
    executeAction: (action: AttributeSchemaUsageActions | BaseActions) => void;
};


export type AttributeSchemaObjectsProps = {
    openSideSheet?: boolean;
    onCloseSideSheet?: () => void;
    setViewOptionState?: (value: ViewOptionStateEnum) => void;
    schemaUsageState: UseAttributeSchemaObjectStateType;
};

function AttributeSchemaObjects({
    openSideSheet,
    onCloseSideSheet,
    setViewOptionState,
    schemaUsageState
}: AttributeSchemaObjectsProps, ref: Ref<unknown> | undefined) {

    const isPhone = isMobile && !isTablet;

    const theme = useTheme();

    const deviceUsageList = useRef<AttributeSchemaObjectListType>(undefined);
    const tankUsageList = useRef<AttributeSchemaObjectListType>(undefined);
    const wellUsageList = useRef<AttributeSchemaObjectListType>(undefined);
    const tankBatteryUsageList = useRef<AttributeSchemaObjectListType>(undefined);

    useImperativeHandle<unknown, AttributeSchemaObjectsType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const {
        state, 
        setActiveTab,
    } = schemaUsageState;
    
    const {
        getValues,
        watch,
        //trigger,
        //control,
        //reset,
        //setValue,
        //resetField
    } = useFormContext<AttributeSchemaFormType>();    



    const [
        includeDevices,
        includeTanks,
        includeWells,
        includeTankBatteries
    ] = getValues([
        'includeDevices',
        'includeTanks',
        'includeWells',
        'includeTankBatteries'
    ]);

    const [devices,
        tanks,
        tankBatteries,
        wells,
    ] =  watch([
        'devices',
        'tanks',
        'tankBatteries',
        'wells'
    ]);


    const deviceCount = includeDevices ? devices.length : 0;
    const wellCount = includeWells ? wells.length : 0;
    const tankCount = includeTanks ? tanks.length : 0;
    const tankBatteryCount = includeTankBatteries ? tankBatteries.length : 0;

    const count = deviceCount +
                    wellCount +
                    tankCount +
                    tankBatteryCount;


    const deviceSelectedCount = devices.filter(o => o.selected === true).length;
    const wellSelectedCount = wells.filter(o => o.selected === true).length;
    const tankSelectedCount = tanks.filter(o => o.selected === true).length;
    const tankBatterySelectedCount = tankBatteries.filter(o => o.selected === true).length;

    
    function renderSection(objectType: eLynxObjectTypeEnum,
        ref: MutableRefObject<AttributeSchemaObjectListType | undefined>
    ) {
        return (
            <AttributeSchemaObjectList 
                ref={ref}
                objectType={objectType}
                openSideSheet={openSideSheet}
                onCloseSideSheet={onCloseSideSheet}
                setViewOptionState={setViewOptionState}
                schemaUsageState={schemaUsageState}
            />
        )
    }


    function executeAction(action: AttributeSchemaUsageActions | BaseActions) {

        switch(state.activeTab) {
            case AttributeSchemaObjectTabEnum.Devices:
                deviceUsageList.current?.executeAction(action);
                break;
            case AttributeSchemaObjectTabEnum.Tanks:
                tankUsageList.current?.executeAction(action);                
                break;

            case AttributeSchemaObjectTabEnum.Wells:
                wellUsageList.current?.executeAction(action);                
                break;

            case AttributeSchemaObjectTabEnum.TankBatteries:
                tankBatteryUsageList.current?.executeAction(action);                
                break;

            default:
                break;
        }
    }

    function onExecuteAction(menuItem: ActionMenuItem) {

        const action = menuItem.action as AttributeSchemaUsageActions | BaseActions;
        executeAction(action);
    }

    useEffect(() => {


        if (count > 0 && state.activeTab === AttributeSchemaObjectTabEnum.None) {
            let activeTab = AttributeSchemaObjectTabEnum.Devices;
            if (deviceCount === 0) {
                if (tankCount > 0) {
                    activeTab = AttributeSchemaObjectTabEnum.Tanks;
                } else if (tankBatteryCount > 0) {
                    activeTab = AttributeSchemaObjectTabEnum.TankBatteries;
                } else {
                    activeTab = AttributeSchemaObjectTabEnum.Wells;
                }
            }
            setActiveTab(activeTab);
        }
    }, [
        count,
        state.activeTab,
        deviceCount,
        wellCount,
        tankCount,
        tankBatteryCount,
        setActiveTab
    ]);


   return (count > 0 && state.activeTab !== AttributeSchemaObjectTabEnum.None ? <Box
       sx={{
           display: 'flex',
           flexDirection: 'column',
           flex: '1 1 auto',
           overflow: 'hidden',
           position: 'relative'
       }}>

       <TabBar  value={state.activeTab}
           onChange={(_event, value) => setActiveTab(value)}
       >
           {deviceCount > 0 && 
               <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Device, true)} 
               count={deviceSelectedCount}
               //countDisabled={activeTab !== SelectColumnsStepTabEnum.DataValues}
               value={AttributeSchemaObjectTabEnum.Devices}
           />
           }

           {tankCount > 0 && 
           <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Tank, true)} 
               count={tankSelectedCount}
               //countDisabled={activeTab !== SelectColumnsStepTabEnum.Attributes}
               value={AttributeSchemaObjectTabEnum.Tanks}
           />
           }

           {tankBatteryCount > 0 && 
           <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.TankBattery, true)} 
               count={tankBatterySelectedCount}
               //countDisabled={activeTab !== SelectColumnsStepTabEnum.Attributes}
               value={AttributeSchemaObjectTabEnum.TankBatteries}
           />
           }

           {wellCount > 0 &&  
           <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Well, true)} 
               count={wellSelectedCount}
               //countDisabled={activeTab !== SelectColumnsStepTabEnum.Attributes}
               value={AttributeSchemaObjectTabEnum.Wells}
           />
           }


       </TabBar>

       {!isPhone ? 
           // NOTE this has to position absolute, othwerwise the Tab component throws errors
           <Box
               sx={{
                   position: 'absolute',
                   top: theme.spacing(1),
                   right: theme.spacing(3)
               }}>

               {usageActionItems.map(o => 
                       <Button key={o.action} onClick={() => onExecuteAction(o)}>
                           {o.label}
                       </Button>
               )}
           </Box> : null }



       <TabContainer visible={state.activeTab === AttributeSchemaObjectTabEnum.Devices}>
           {renderSection(eLynxObjectTypeEnum.Device, deviceUsageList)}
       </TabContainer>


       <TabContainer visible={state.activeTab === AttributeSchemaObjectTabEnum.Wells}>
           {renderSection(eLynxObjectTypeEnum.Well, wellUsageList)}
       </TabContainer>

       <TabContainer visible={state.activeTab === AttributeSchemaObjectTabEnum.Tanks}>
           {renderSection(eLynxObjectTypeEnum.Tank, tankUsageList)}
       </TabContainer>

       <TabContainer visible={state.activeTab === AttributeSchemaObjectTabEnum.TankBatteries}>
           {renderSection(eLynxObjectTypeEnum.TankBattery, tankBatteryUsageList)}
       </TabContainer>

   </Box> : count === 0 ?
   
       <Box sx={{
           padding: 1
       }}>
           <NoData text={t('setpointAdmin.nodata.noObjects')} />
       </Box>
   : null);
  }


  export default forwardRef(AttributeSchemaObjects);
